import * as Sentry from '@sentry/react'

export const initSentry = () => {
  // Only initialize Sentry in production, not in dev/local
  const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_MODE === 'dev'
  
  if (isDevelopment) {
    console.log('Sentry disabled in development mode')
    return
  }
  
  const dsn = import.meta.env.VITE_SENTRY_DSN
  
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking disabled.')
    return
  }

  Sentry.init({
    dsn,
    environment: 'backoffice', // Same as Flutter app
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION,
    beforeSend(event, hint) {
      const error = hint.originalException as Error | undefined
      // Don't send React hydration errors
      if (error?.message?.includes('Hydration')) {
        return null
      }
      
      // Sanitize sensitive data
      if (event.request) {
        // Remove auth headers
        if (event.request.headers) {
          delete event.request.headers['Authorization']
          delete event.request.headers['authorization']
        }
        // Remove cookies
        if (event.request.cookies) {
          delete event.request.cookies
        }
      }
      
      return event
    },
  })

  // Set user context if available
  const userString = localStorage.getItem('user')
  if (userString) {
    try {
      const user = JSON.parse(userString)
      Sentry.setUser({
        id: user.uid,
        email: user.email,
      })
    } catch (error) {
      console.error('Failed to parse user for Sentry context', error)
    }
  }
}

export const captureException = (error: Error, context?: Record<string, any>) => {
  console.error('Error captured:', error)
  
  if (context) {
    Sentry.withScope((scope) => {
      Object.keys(context).forEach(key => {
        scope.setContext(key, context[key])
      })
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level)
}